import CustomButton from '@/components/atoms/CustomButton';
import MobileFirstPageTemplate from '@/components/templates/MobileFirstPageTemplate';
import { MOBILE_DEFAULT_PADDING } from '@/constants/ui';
import useIsWeb from '@/hooks/useIsWeb';
import OrganizationWholenessRingExample from '@/modules/try-screener/OrganizationWholenessRingExample';
import colors from '@/theme/colors';
import { Image, Input, Text, VStack } from '@chakra-ui/react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { URL_REGEX } from '@/constants/stringVars';
import useCheckOrg from '@/hooks/publicscreener/useCheckOrg';
import { useState } from 'react';
import TurnstileWidget from '@/components/molecules/TurnstileWidget';
import iconsPng from '@/assets/img/png/icons';
import { useEffect } from 'react';
import useTelemetry from '@/providers/TelemetryProvider/useTelemetry';
import { mixpanelEvents, PUBLIC_SCREENER_TYPE } from '@/constants/mixpanel';

const ORG_URL_MIN_LENGTH = 5;

const schema = yup
  .object()
  .shape({
    orgUrl: yup
      .string()
      .required('Organization website URL is required.')
      .min(
        ORG_URL_MIN_LENGTH,
        `Organization website URL (web address) must be at least ${ORG_URL_MIN_LENGTH} characters long.`,
      )
      .matches(URL_REGEX, 'Must be valid website URL. Example: www.fire.slc'),
  })
  .required();

const TryScreenerLandingPage = () => {
  const navigate = useNavigate();
  const { trackEvent } = useTelemetry();

  const isWeb = useIsWeb();

  useEffect(() => {
    trackEvent(mixpanelEvents.SCREENER_PAGE_VIEWED, { type: PUBLIC_SCREENER_TYPE });
  }, []);

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const { register, formState, handleSubmit } = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
  });

  const { isValid, errors } = formState;

  const navigateToOrgSlug = (orgSlug: string) =>
    navigate({
      to: `/try-screener/$organizationSlug`,
      params: { organizationSlug: orgSlug },
    });

  const { mutate, isPending } = useCheckOrg(navigateToOrgSlug);

  const onSubmit = ({ orgUrl }: { orgUrl: string }) => {
    const normalized = orgUrl.startsWith('http://') || orgUrl.startsWith('https://') ? orgUrl : `https://${orgUrl}`;
    const url = new URL(normalized).toString();

    trackEvent(mixpanelEvents.SCREENER_ORG_URL_SUBMITTED, { org_url: url, type: PUBLIC_SCREENER_TYPE });

    mutate({ url, turnstileToken }); // Pass the turnstile token to the mutation
  };

  return (
    <MobileFirstPageTemplate>
      <VStack gap={'40px'} width={'100%'} padding={MOBILE_DEFAULT_PADDING}>
        <Image src={iconsPng.notepadWithPenOnScreen} width={140} height={140} alt="notepadWithPenOnScreen" />
        <VStack maxWidth={'360px'} gap={'20px'}>
          <Text variant={'loraExtraSmallTitle'} color={'text.mediumBlue'} maxWidth={'300px'}>
            Take the Wholeness Screener anonymously
          </Text>
          <VStack gap={'8px'}>
            <Text variant={'urbanistSemiBoldRegular'} color={'text.mediumBlue'} textAlign={'center'}>
              Your insights contribute to a confidential organizational report that helps leaders identify meaningful
              ways to better support your team’s well-being.
            </Text>
            <Text variant={'urbanistSemiBoldRegular'} color={'text.mediumBlue'} textAlign={'center'}>
              Try it yourself and share it with your coworkers to spark positive change.
            </Text>
          </VStack>
        </VStack>
        <Text
          variant={'urbanistBold'}
          backgroundColor={'primary.500'}
          padding={'12px'}
          color={'extra.white'}
          cursor={'default'}
          borderRadius={'8px'}
          width={isWeb ? undefined : '100%'}
          textAlign={'center'}
        >
          100% Anonymous & Confidential
        </Text>

        <VStack gap={'20px'} maxWidth={'300px'}>
          <Text variant={'urbanistSemiBoldRegular'} textAlign={'center'}>
            Get started by entering your organization’s website address.
          </Text>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
            <VStack>
              <Input
                width={'100%'}
                height={'54px'}
                {...register('orgUrl')}
                backgroundColor={'white'}
                placeholder="Example: www.fire.slc.gov or slc.gov"
                _placeholder={{ fontWeight: 600, color: colors.text.mediumGray }}
                autoComplete="off"
              />
              {!isValid && <Text variant={'error'}>{errors.orgUrl?.message}</Text>}
              <CustomButton
                label="Continue"
                backgroundColor="secondary.500"
                labelColor="extra.white"
                disabled={!isValid || !turnstileToken}
                buttonType="submit"
                width={'100%'}
                isLoading={isPending}
              />
              <TurnstileWidget setTurnstileToken={setTurnstileToken} turnstileToken={turnstileToken} />
            </VStack>
          </form>
        </VStack>
      </VStack>
      <OrganizationWholenessRingExample />
    </MobileFirstPageTemplate>
  );
};

export const Route = createFileRoute('/try-screener/')({
  staticData: { title: 'Try the Screener' },
  component: TryScreenerLandingPage,
  beforeLoad: async ({ context: { auth } }) => {
    const isAuthenticated = auth?.isAuthenticated;
    if (isAuthenticated) {
      throw redirect({
        to: '/',
      });
    }
  },
});
